import React, { useId, useMemo } from 'react';
import type { RectMm } from '../../layout/doorLayout';
import type { DoorGlazingSelection, DoorHardwareSelection, DoorSlabConfiguration } from '../../types/doorConfiguration';
import { PLACEHOLDER_SOLID_SLAB_STYLE_ID } from '../../types/doorConfiguration';
import { doorStyleDefinitionFrom } from '../../assets/doorStyleDefinition';
import { getExternalSlabStyle } from '../../assets/externalSlabCatalogue';
import { getGlassDesign, selectedGlassDesignId } from '../../assets/glassCatalogue';
import { applyDoorColourToSlabArtwork } from '../../colour/slabColourPaletteCss';
import { hideAuthoredGlassSlots } from '../../colour/hideAuthoredGlassSlots';
import type { DoorViewSide } from '../doorViewSide';
import {
  EXTERNAL_SLAB_STYLE_PLACEHOLDER_SOLID,
  resolveExternalSlabFill,
} from './externalSlabFill';
import {
  EXTERNAL_SLAB_REVEAL_STROKE_MM,
  computeExternalSlabRevealBounds,
  localSlabBounds,
} from './externalSlabGeometry';
import { useExternalSlabArtwork } from './useExternalSlabArtwork';
import { getHardwareProduct } from '../../hardware/hardwareCatalog';
import { sortHardwareForPaint } from '../../hardware/hardwarePaintOrder';
import { ConfiguredHardwareArtwork } from './ConfiguredHardwareArtwork';
import { GlazingLayer } from './GlazingLayer';
import { useGlassArtwork } from './useGlassArtwork';
import {
  HardwareGuidesDebugOverlay,
  shouldShowHardwareGuideDebug,
} from './HardwareGuidesDebugOverlay';

export const EXTERNAL_SLAB_VIEW_SIDE: DoorViewSide = 'external';

export interface ExternalSlabRendererProps {
  slab: DoorSlabConfiguration;
  /** Layout-engine millimetre opening. Sole source of slab x, y, width, and height. */
  slabOpeningBounds: RectMm;
  viewSide?: DoorViewSide;
  /** Resolved finish for the current view. Defaults to the external slab colour. */
  colourId?: string;
  glazing?: DoorGlazingSelection[];
  /** External-face hardware from the saved configuration. Internal furniture is independent later. */
  hardware?: DoorHardwareSelection[];
  /** Override development-only hardware guide markers. Default: NODE_ENV === 'development'. */
  debugHardwareGuides?: boolean;
}

function rectProps(bounds: RectMm) {
  return {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
}

/**
 * External slab inside `slabOpeningBounds`. Catalogue artwork is stretched to
 * that opening. Authored canvas size never changes layout geometry.
 * Palette HEX is applied only to marked door-colour elements. Glass is clipped
 * to `#glazing-guides`. `#hardware-guides` is placement metadata only and is
 * never painted; development may overlay debug markers from the parsed geometry.
 */
export const ExternalSlabRenderer: React.FC<ExternalSlabRendererProps> = ({
  slab,
  slabOpeningBounds,
  viewSide = EXTERNAL_SLAB_VIEW_SIDE,
  colourId,
  glazing = [],
  hardware = [],
  debugHardwareGuides,
}) => {
  const clipUid = useId().replace(/:/g, '');
  const catalogueStyle = getExternalSlabStyle(slab.styleId);
  const artwork = useExternalSlabArtwork(catalogueStyle?.publicPath ?? null);
  const local = localSlabBounds(slabOpeningBounds);
  const fill = resolveExternalSlabFill(colourId ?? slab.colourId);
  const colouredMarkup = useMemo(
    () => (artwork ? applyDoorColourToSlabArtwork(artwork.markup, fill) : null),
    [artwork, fill]
  );
  const definition = catalogueStyle ? doorStyleDefinitionFrom(catalogueStyle, artwork) : null;
  const glassDesign = getGlassDesign(selectedGlassDesignId(glazing));
  const showDynamicGlazing = Boolean(glassDesign && definition && definition.glazingSlots.length > 0);
  const glassPaintId = `${clipUid}-${glassDesign?.id ?? 'glass'}`;
  const glassArtwork = useGlassArtwork(
    showDynamicGlazing && glassDesign ? glassDesign.publicPath : null,
    glassPaintId
  );
  const useArtwork = Boolean(catalogueStyle && artwork && colouredMarkup);
  const bodyMarkup = useMemo(() => {
    if (!colouredMarkup) return null;
    return showDynamicGlazing && glassArtwork?.href
      ? hideAuthoredGlassSlots(colouredMarkup)
      : colouredMarkup;
  }, [colouredMarkup, glassArtwork, showDynamicGlazing]);
  const styleId = catalogueStyle?.id ?? PLACEHOLDER_SOLID_SLAB_STYLE_ID;
  const reveal = !useArtwork ? computeExternalSlabRevealBounds(slabOpeningBounds) : null;
  const showHardwareDebug = shouldShowHardwareGuideDebug(debugHardwareGuides);
  const placedHardware = useMemo(() => {
    const items = hardware.flatMap((selection, configIndex) => {
      const product = getHardwareProduct(selection.id);
      if (!product || !selection.placementId) return [];
      return [{ product, placementId: selection.placementId, configIndex }];
    });
    return sortHardwareForPaint(items);
  }, [hardware]);
  const occupiedAnchorIds = placedHardware.map((item) => item.placementId);

  return (
    <svg
      data-part="door-slab"
      data-view-side={viewSide}
      data-slab-style={styleId}
      data-slab-fill={useArtwork ? 'artwork' : 'placeholder'}
      data-door-colour={fill}
      x={slabOpeningBounds.x}
      y={slabOpeningBounds.y}
      width={slabOpeningBounds.width}
      height={slabOpeningBounds.height}
      viewBox={`0 0 ${slabOpeningBounds.width} ${slabOpeningBounds.height}`}
      overflow="hidden"
    >
      {useArtwork && artwork && bodyMarkup && definition ? (
        <svg
          data-part="door-slab-artwork"
          x={0}
          y={0}
          width={local.width}
          height={local.height}
          viewBox={artwork.viewBox}
          preserveAspectRatio="none"
          overflow="hidden"
          style={{ ['--door-colour' as string]: fill }}
        >
          <g
            data-part="door-slab-body"
            dangerouslySetInnerHTML={{ __html: bodyMarkup }}
          />
          {showDynamicGlazing && glassDesign && glassArtwork?.href ? (
            <GlazingLayer
              glassDesign={glassDesign}
              artwork={glassArtwork}
              slots={definition.glazingSlots}
              clipUid={clipUid}
            />
          ) : null}
          {placedHardware.length > 0 ? (
            <g data-part="door-hardware-layer">
              {placedHardware.map(({ product, placementId }) => (
                <ConfiguredHardwareArtwork
                  key={`${product.id}:${placementId}`}
                  product={product}
                  placementId={placementId}
                  slabGuides={definition.hardwareGuides}
                />
              ))}
            </g>
          ) : null}
          {showHardwareDebug ? (
            <HardwareGuidesDebugOverlay
              guides={definition.hardwareGuides}
              occupiedAnchorIds={occupiedAnchorIds}
            />
          ) : null}
        </svg>
      ) : (
        <>
          <rect data-part="door-slab-base" {...rectProps(local)} fill={fill} />
          {reveal ? (
            <rect
              data-part="door-slab-reveal"
              {...rectProps(reveal)}
              fill="none"
              stroke="#000000"
              strokeOpacity={0.28}
              strokeWidth={EXTERNAL_SLAB_REVEAL_STROKE_MM}
            />
          ) : null}
        </>
      )}
    </svg>
  );
};

export { EXTERNAL_SLAB_STYLE_PLACEHOLDER_SOLID };
